import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  pushPermission,
  pushAlreadySubscribed,
  subscribeToPush,
} from "../lib/push";

const DISMISS_KEY = "vc_push_prompt_dismissed_v1";

/**
 * Soft pre-prompt for push notifications. Mounts inside Room.jsx after the
 * user creates/joins a room. We never trigger the native permission dialog
 * directly — the user has to tap "Allow" first. If they tap "Not now" we
 * stash a dismissal flag so we don't keep nagging.
 */
export default function PushOptInSheet({ open, onClose, partnerName }) {
  const [pending, setPending] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!open) {
      setVisible(false);
      return;
    }
    if (!pushSupported()) return;
    if (pushPermission() !== "default") return; // already granted/denied
    if (pushAlreadySubscribed()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch (_e) {
      /* ignore */
    }
    // Stagger the sheet so it doesn't fight the room-loaded transitions.
    const t = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(t);
  }, [open]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (_e) {
      /* ignore */
    }
    setVisible(false);
    onClose?.();
  };

  const allow = async () => {
    setPending(true);
    const ok = await subscribeToPush();
    setPending(false);
    if (ok) {
      toast.success("Locked in 🔥", {
        description: "We'll buzz you when the vibe shifts.",
        duration: 3000,
      });
      setVisible(false);
      onClose?.();
    } else {
      toast("No worries — you can enable later in Profile.", { duration: 2400 });
      dismiss();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="push-optin-sheet"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center bg-black/55 backdrop-blur-sm px-4 pb-6 sm:pb-0"
          onClick={dismiss}
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border border-pink-500/30 bg-gradient-to-b from-[#1a0e1f] to-[#0b0614] p-6 shadow-[0_0_60px_rgba(255,77,141,0.35)]"
          >
            <button
              onClick={dismiss}
              data-testid="push-optin-dismiss"
              className="absolute right-3 top-3 text-white/40 hover:text-white"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] mb-4 mx-auto shadow-[0_0_30px_rgba(255,77,141,0.5)]">
              <Bell className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-2xl font-display italic text-white text-center leading-tight">
              Get a buzz when they're ready 🔥
            </h2>
            <p className="text-sm text-white/70 text-center mt-3 leading-relaxed">
              {partnerName ? `${partnerName} ` : "Your person "}
              answered? Picked Spicy Mode? Sent a view-once video?
              <br />
              <span className="text-white/90">
                We'll ping your phone the second the vibe shifts.
              </span>
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              <button
                onClick={allow}
                disabled={pending}
                data-testid="push-optin-allow"
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#FF4D8D] to-[#7B61FF] text-white font-semibold shadow-[0_0_24px_rgba(255,77,141,0.4)] hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-60"
              >
                {pending ? "Just a sec…" : "Allow notifications"}
              </button>
              <button
                onClick={dismiss}
                data-testid="push-optin-skip"
                className="w-full py-3 rounded-2xl text-white/60 hover:text-white/90 text-sm transition-colors"
              >
                Not now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
